import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, MessageCircle, AudioLines, Settings as SettingsIcon, LogOut } from 'lucide-react';
import { MimiqueIcon, MimiqueWordmark } from './MimiqueLogo';
import FoundingBadge from './FoundingBadge';
import PlanBadge from './PlanBadge';

const NAV_ITEMS = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/collections', label: 'Phrases', icon: MessageCircle },
    // Only for accounts with sound lessons switched on (/auth/me sound_lessons).
    { to: '/sounds', label: 'Sound lessons', short: 'Lessons', icon: AudioLines, lessons: true },
    { to: '/settings', label: 'Settings', icon: SettingsIcon },
];

const Sidebar = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const items = NAV_ITEMS.filter(i => !i.lessons || user?.sound_lessons);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <>
            {/* ── Desktop sidebar — hidden on mobile ───────────────────── */}
            <aside className="hidden md:flex fixed top-0 left-0 h-screen w-60 bg-white border-r border-stone-200 flex-col z-30">
                <div className="px-5 py-5 border-b border-stone-200">
                    <div className="flex items-center gap-2">
                        <MimiqueIcon size={34} variant="square" />
                        <h1 className="leading-none">
                            <MimiqueWordmark colorA="#1a5235" colorB="#4da673" fontSize={21} />
                        </h1>
                    </div>
                    <p className="text-xs text-stone-500 mt-1 truncate">{user?.name}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <PlanBadge plan={user?.plan} />
                        {user?.founding_member && <FoundingBadge />}
                    </div>
                </div>

                <nav className="flex-1 px-3 py-4 space-y-0.5">
                    {items.map(({ to, label, icon: Icon }) => (
                        <NavLink
                            key={to}
                            to={to}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    isActive
                                        ? 'bg-primary text-white'
                                        : 'text-stone-600 hover:bg-stone-100 hover:text-foreground'
                                }`
                            }
                        >
                            <Icon className="h-4 w-4 shrink-0" />
                            {label}
                        </NavLink>
                    ))}
                </nav>

                <div className="px-3 pb-5 border-t border-stone-200 pt-4">
                    <button
                        data-testid="sidebar-logout-btn"
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-100 hover:text-foreground transition-colors w-full"
                    >
                        <LogOut className="h-4 w-4 shrink-0" />
                        Sign Out
                    </button>
                </div>
            </aside>

            {/* ── Mobile bottom nav — hidden on desktop ────────────────── */}
            <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-stone-200 safe-area-inset-bottom">
                <div className="flex">
                    {items.map(({ to, label, short, icon: Icon }) => (
                        <NavLink
                            key={to}
                            to={to}
                            className={({ isActive }) =>
                                `flex-1 flex flex-col items-center justify-center py-2.5 gap-1 min-h-[56px] transition-colors ${
                                    isActive ? 'text-primary' : 'text-stone-400'
                                }`
                            }
                        >
                            <Icon className="h-5 w-5" />
                            <span className="text-[10px] font-medium leading-none">{short || label}</span>
                        </NavLink>
                    ))}
                    <button
                        data-testid="sidebar-logout-btn"
                        onClick={handleLogout}
                        className="flex-1 flex flex-col items-center justify-center py-2.5 gap-1 min-h-[56px] text-stone-400 transition-colors"
                    >
                        <LogOut className="h-5 w-5" />
                        <span className="text-[10px] font-medium leading-none">Sign Out</span>
                    </button>
                </div>
            </nav>
        </>
    );
};

export default Sidebar;
